import { db } from "@/services/config";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  documentId,
} from "firebase/firestore";
import type { BlockedUserRecord } from "./usersBlocked.types";
import { getOppositeGender } from "@/utils";

type AutocompleteUser = {
  uid: string;
  username: string;
  profileImage: string | null;
  city?: string;
  state?: string;
};

export class BlockService {
  private collectionRef = collection(db, "users_blocked");
  private usersCollectionRef = collection(db, "users");

  private getDocId(blockerId: string, blockedId: string) {
    return `${blockerId}_${blockedId}`;
  }

  async blockUser(
    blockerId: string,
    blockedId: string,
    blockedData: { username: string; profileImage: string | null },
  ) {
    const docId = this.getDocId(blockerId, blockedId);
    const mirrorDocId = this.getDocId(blockedId, blockerId);

    // outgoing: registro "real" (aparece na lista de bloqueados do blocker)
    // incoming: registro espelho (permite filtro 2 vias sem precisar query por blockedId)
    // Buscar dados do bloqueador para o registro espelhado
    let blockerData = { username: "", profileImage: null };
    try {
      const blockerSnap = await getDoc(doc(this.usersCollectionRef, blockerId));
      if (blockerSnap.exists()) {
        const d = blockerSnap.data();
        blockerData = {
          username: d.username || "",
          profileImage: d.profileImage || null,
        };
      }
    } catch {}

    await Promise.all([
      setDoc(doc(this.collectionRef, docId), {
        id: docId,
        blockerId,
        blockedId,
        kind: "outgoing",
        blockedUserData: blockedData,
        createdAt: serverTimestamp(),
      } satisfies Partial<BlockedUserRecord>),
      setDoc(
        doc(this.collectionRef, mirrorDocId),
        {
          id: mirrorDocId,
          blockerId: blockedId,
          blockedId: blockerId,
          kind: "incoming",
          blockedUserData: blockerData,
          createdAt: serverTimestamp(),
        } satisfies Partial<BlockedUserRecord>,
        { merge: true },
      ),
    ]);
  }

  async unblockUser(blockerId: string, blockedId: string) {
    const docId = this.getDocId(blockerId, blockedId);
    const mirrorDocId = this.getDocId(blockedId, blockerId);
    await Promise.all([
      deleteDoc(doc(this.collectionRef, docId)),
      deleteDoc(doc(this.collectionRef, mirrorDocId)),
    ]);
  }

  async isBlocked(blockerId: string, blockedId: string): Promise<boolean> {
    const snap = await getDoc(
      doc(this.collectionRef, this.getDocId(blockerId, blockedId)),
    );
    return snap.exists();
  }

  async listBlockedIds(blockerId: string): Promise<string[]> {
    const q = query(this.collectionRef, where("blockerId", "==", blockerId));
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((d) => d.data()?.blockedId)
      .filter((v): v is string => typeof v === "string" && v.length > 0);
  }

  async listBlockedUsers(blockerId: string): Promise<BlockedUserRecord[]> {
    const q = query(this.collectionRef, where("blockerId", "==", blockerId));
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((d) => d.data() as BlockedUserRecord)
      .filter((r) => r.kind !== "incoming");
  }

  /**
   * Autocomplete por nickname (mesmas regras do feed):
   * - closeAccount == false
   * - awaitingApproval == false
   * - visible != false
   * - gender == mapping(seeking)
   * - exclui o próprio usuário
   */
  async listAutocompleteUsers(params: {
    blockerId: string;
    gender: string;
    keyword: string;
    pageSize?: number;
  }): Promise<AutocompleteUser[]> {
    const keyword = String(params.keyword || "")
      .trim()
      .toLowerCase();
    const pageSize = typeof params.pageSize === "number" ? params.pageSize : 10;
    if (!keyword || keyword.length < 2) return [];

    const genderToSearch = getOppositeGender(params.gender);

    // Carrega IDs bloqueados para já excluir do autocomplete
    const blockedIds = new Set(await this.listBlockedIds(params.blockerId));

    const q = query(
      this.usersCollectionRef,
      where("closeAccount", "==", false),
      where("awaitingApproval", "==", false),
      where("gender", "in", genderToSearch),
      where(documentId(), "!=", params.blockerId),
      orderBy("__name__"),
      limit(pageSize * 8),
    );

    const snapshot = await getDocs(q);

    return snapshot.docs
      .filter((d) => d.data()?.visible !== false)
      .filter((d) => !blockedIds.has(d.id))
      .map((d) => {
        const data = d.data();
        return {
          uid: d.id,
          username: String(data.username || ""),
          profileImage: (data.profileImage as string | null) || null,
          city: data.city || "",
          state: data.state || "",
        };
      })
      .filter((u) => u.username.toLowerCase().includes(keyword))
      .slice(0, pageSize);
  }
}

export const blockService = new BlockService();
